import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { Status } from 'model/alerts.model';
import { BusinessesService, GetBusinessesQueryParamsType } from 'service/businesses.service';

export type BusinessType = {
  businessId: string;
  name: string;
  logoUrl: string;
  category: string;
  industry: string;
  activeDealCount: number;
  activeDeals: any[];
  wonValue: number;
  clientFitScore: number;
  tags: any[];
  lists: string[];
  website: string;
  country: string;
  employeeCount: number;
  revenue: number;
  description: string;
  createdAt: string;
  updatedAt: string;
  successRate: number;
  ticker: string;
  employees: number;
  isDeleted: boolean;
  zoominfoCompanyId: string;
  ceo: string;
  createdBy: string;
  lastEnrichedAt: string;
  createdById: string;
  headquarters: string;
  foundedYear: number;
  annualRevenue: number;
  totalDealCount: number;
  avgVelocity: number | null;
  ourContacts: number;
  isFollowing: boolean;
  isTargeted: boolean;
};

export type CategoryType = { name: string; count: number };

export type PaginationType = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

type BusinessInitialStateType = {
  businesses: BusinessType[];
  categories: CategoryType[];
  myBusinessesCount: number;
  allBusinessesCount: number;
  pagination: PaginationType;
  loading: Status;
  tabValue: number;
  categoryValue: number;
  adminSearchModal: boolean;
  searchTerm: '';
  sortBy: `name` | `activeDeals` | `totalSpend` | `clientFit`;
  sortOrder: 'asc' | 'desc';
};

const initialState: BusinessInitialStateType = {
  businesses: [],
  categories: [],
  myBusinessesCount: 0,
  allBusinessesCount: 0,
  pagination: {
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  },
  loading: 'idle',
  tabValue: 0,
  categoryValue: 0,
  adminSearchModal: false,
  searchTerm: '',
  sortBy: 'name',
  sortOrder: 'asc',
};

export const fetchBusinesses = createAsyncThunk(
  'businesses/fetchBusinesses',
  async (params?: GetBusinessesQueryParamsType) => {
    const data = await BusinessesService.getBusinesses(params);
    return data;
  },
);

export const fetchBusinessesByID = createAsyncThunk(
  'businesses/fetchDealById',
  async (id: string) => {
    const data = await BusinessesService.getBusinessById(id);
    return data.data;
  },
);

const businessesSlice = createSlice({
  name: 'businessesSlice',
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setTabValue: (state, action) => {
      state.tabValue = action.payload;
    },
    setCategoryValue: (state, action) => {
      state.categoryValue = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
    },
    setAdminSearchModal: (state, action) => {
      state.adminSearchModal = action.payload;
    },
    setSearchTerm: (state, action) => {
      state.searchTerm = action.payload;
    },
    setSortBy: (state, action) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action) => {
      state.sortOrder = action.payload;
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchBusinesses.fulfilled, (state, action) => {
      const {
        businesses,
        categories,
        myBusinessesCount,
        allBusinessesCount,
        page,
        limit,
        totalPages,
        total,
      } = action.payload;

      state.businesses = businesses;

      state.categories = [...categories];
      state.myBusinessesCount = myBusinessesCount;
      state.allBusinessesCount = allBusinessesCount;
      state.pagination = {
        pageSize: limit,
        page,
        totalPages,
        total,
      };
      state.loading = 'complete';
    });
    builder.addCase(fetchBusinesses.pending, (state) => {
      state.loading = 'in-progress';
    });
  },
});

export const {
  setPage,
  setTabValue,
  setPageSize,
  setAdminSearchModal,
  setSearchTerm,
  setSortBy,
  setSortOrder,
  setCategoryValue,
} = businessesSlice.actions;
export default businessesSlice.reducer;
